import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

const LanguagePicker = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && 
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    // Update HTML lang attribute
    document.documentElement.lang = langCode;
  };

  const getStyles = () => {
    if (variant === 'transparent') {
      return {
        button: 'text-white/80 hover:text-white bg-white/10 hover:bg-white/20 border-white/20',
        dropdown: 'bg-black/90 backdrop-blur-lg border-white/20',
        item: 'text-white hover:bg-white/10',
        itemActive: 'bg-white/20 text-white'
      };
    }
    return {
      button: 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300',
      dropdown: 'bg-white border-gray-200 shadow-lg',
      item: 'text-gray-700 hover:bg-gray-100',
      itemActive: 'bg-purple-50 text-purple-600'
    };
  };

  const styles = getStyles();

  // Render dropdown using portal to avoid overflow clipping
  const dropdownContent = isOpen && ReactDOM.createPortal(
    <div 
      ref={dropdownRef}
      className={`fixed w-48 rounded-xl ${styles.dropdown} border shadow-xl overflow-hidden`}
      style={{ 
        top: dropdownPosition.top,
        right: dropdownPosition.right,
        zIndex: 99999,
      }}
    >
      <div className="py-1">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${
              currentLanguage.code === lang.code ? styles.itemActive : styles.item
            }`}
          >
            <span className="mr-3 text-lg">{lang.flag}</span>
            <span className="flex-1 text-left">{lang.name}</span>
            {currentLanguage.code === lang.code && (
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>,
    document.body
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${styles.button}`}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <GlobeAltIcon className="h-4 w-4 mr-2" />
        <span className="mr-1">{currentLanguage.flag}</span>
        <span className="hidden sm:inline">{currentLanguage.name}</span>
        <ChevronDownIcon className={`h-4 w-4 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {dropdownContent}
    </div>
  );
};

export default LanguagePicker;

